import {
  AuditCategory,
  AuditFinding,
  AuditSeverity,
  ApiDiffFinding,
} from "../audit-report";
import { AuditCheckRunner, AuditCheckRunnerContext } from "../run-scan";

/**
 * One public export parsed out of an `.api.md` rollup. The signature
 * is the verbatim declaration text from the rollup (TypeScript
 * declaration form), with internal whitespace normalized to single
 * spaces so cosmetic re-formatting between runs does not surface as
 * a `changed` finding.
 */
export interface ApiSurfaceEntry {
  /**
   * Public name of the export (function name, class name, etc.).
   */
  readonly name: string;

  /**
   * Verbatim declaration line from the rollup with whitespace
   * collapsed. Used as the equality comparison for `added` /
   * `removed` / `changed` classification.
   */
  readonly signature: string;
}

/**
 * Result of diffing a baseline rollup against a current rollup. Each
 * field carries the API entries that fall into that change bucket.
 * Used by {@link createApiDiffCheck} to emit one finding per entry,
 * but exported separately so callers that want to render or
 * post-process the diff outside the audit-report shape can.
 */
export interface ApiDiffResult {
  /** Exports present in the current rollup but not the baseline. */
  readonly added: ReadonlyArray<ApiSurfaceEntry>;

  /** Exports present in the baseline but not the current rollup. */
  readonly removed: ReadonlyArray<ApiSurfaceEntry>;

  /**
   * Exports present in both rollups but whose declaration text
   * differs (signature change, type-parameter change, etc.). The
   * carried entry is the **current** rollup's view of the symbol so
   * downstream consumers can show the new signature.
   */
  readonly changed: ReadonlyArray<ApiSurfaceEntry>;
}

/**
 * Options accepted by {@link createApiDiffCheck}. The runner
 * consumes already-fetched rollup contents — the orchestrator is
 * responsible for running the `extract-api.sh` helper against the
 * current ref and against the configured baseline ref before
 * dispatching to the runner. Passing the rollups in as strings keeps
 * tests cheap (inline fixtures) and lets the same runner shape
 * cover the audit-mode case where the baseline is an empty rollup
 * (every export shows up as `added`).
 */
export interface ApiDiffCheckOptions {
  /**
   * Verbatim contents of the `.api.md` rollup the orchestrator
   * extracted against the **baseline** ref (typically `origin/main`
   * for `pr` mode or empty for `audit` mode). May be the empty
   * string when no baseline is available — every current export
   * then surfaces as `added`.
   */
  readonly baselineRollup: string;

  /**
   * Verbatim contents of the `.api.md` rollup the orchestrator
   * extracted against the **current** ref (the working tree the
   * scan is running over).
   */
  readonly currentRollup: string;

  /**
   * Path attached to every finding's `location.file`. Conventionally
   * the path to the `.api.md` file relative to the repo root (e.g.
   * `packages/@codedrifters/configulator/.api-extractor/configulator.api.md`)
   * so consumers can jump from a finding back to the source rollup.
   * Defaults to the empty string, meaning the finding has no
   * concrete file anchor.
   *
   * @default ""
   */
  readonly rollupPath?: string;

  /**
   * Stable name surfaced via {@link AuditCheckRunner.name}. Defaults
   * to `"apiDiff"` so logs match the audit-report category key.
   *
   * @default "apiDiff"
   */
  readonly name?: string;
}

/**
 * Parse a `.api.md` rollup into the set of public-API entries it
 * declares. The parser is intentionally line-oriented and tolerant
 * of api-extractor's exact formatting:
 *
 * 1. Find the first fenced `ts` code block (api-extractor wraps the
 *    rollup in one). Lines outside the block are ignored.
 * 2. Walk the block linewise. Every line that begins with `export`
 *    is the start of a declaration; subsequent lines that do not
 *    begin with `export`, `//`, `}`, or `import` are appended until
 *    a terminator (`;`, top-level `}`) closes the declaration.
 * 3. Extract the public name from the start of the declaration —
 *    the first identifier after `export {function|class|interface|...}`
 *    (or the first name in an `export { A, B }` clause).
 * 4. Normalize whitespace inside the captured signature to a single
 *    space so re-flowing the rollup does not produce spurious
 *    `changed` findings.
 *
 * Lines containing only `// @public`, `// @beta`, etc. are stripped
 * — they are release-tag annotations the extractor emits, and they
 * change independently of the signature itself.
 */
export function parseApiRollup(rollup: string): ReadonlyArray<ApiSurfaceEntry> {
  const fenceContents = extractFirstTsFence(rollup);
  if (fenceContents === undefined) {
    return [];
  }

  const lines = fenceContents.split("\n");
  const entries: Array<ApiSurfaceEntry> = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed.startsWith("export ")) {
      i += 1;
      continue;
    }

    // Capture this declaration. Multi-line declarations are joined
    // with a single space so signature comparison is order-stable.
    const startIndex = i;
    let buffer = trimmed;
    let braceDepth = countBraceDelta(trimmed);
    let endsWithSemicolon = trimmed.endsWith(";");
    i += 1;
    while (
      i < lines.length &&
      !isComplete(buffer, braceDepth, endsWithSemicolon)
    ) {
      const next = lines[i];
      const nextTrimmed = next.trim();
      if (nextTrimmed.length === 0 || nextTrimmed.startsWith("//")) {
        i += 1;
        continue;
      }
      buffer = `${buffer} ${nextTrimmed}`;
      braceDepth += countBraceDelta(nextTrimmed);
      endsWithSemicolon = nextTrimmed.endsWith(";");
      i += 1;
    }

    const normalized = normalizeWhitespace(buffer);
    const name = extractDeclarationName(normalized);
    if (name) {
      entries.push({ name, signature: normalized });
    }

    // Guard against an `export { … }` block that contains multiple
    // names — split it into one entry per name.
    if (name && normalized.startsWith("export {")) {
      const names = extractExportListNames(normalized);
      if (names.length > 1) {
        // Replace the single entry with one per name.
        entries.pop();
        for (const exportName of names) {
          entries.push({ name: exportName, signature: normalized });
        }
      }
    }

    // Defensive: never fail to advance.
    if (i === startIndex) {
      i += 1;
    }
  }

  // Deduplicate by name — api-extractor emits one declaration per
  // public symbol, but in pathological inputs a name could repeat.
  const dedup = new Map<string, ApiSurfaceEntry>();
  for (const entry of entries) {
    if (!dedup.has(entry.name)) {
      dedup.set(entry.name, entry);
    }
  }
  return Array.from(dedup.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

/**
 * Diff a baseline rollup against a current rollup and bucket every
 * public export into `added`, `removed`, or `changed`. Exported so
 * callers can compute the diff without going through the runner
 * shape.
 */
export function diffApiRollups(
  baselineRollup: string,
  currentRollup: string,
): ApiDiffResult {
  const baseline = new Map<string, ApiSurfaceEntry>();
  for (const entry of parseApiRollup(baselineRollup)) {
    baseline.set(entry.name, entry);
  }
  const current = new Map<string, ApiSurfaceEntry>();
  for (const entry of parseApiRollup(currentRollup)) {
    current.set(entry.name, entry);
  }

  const added: Array<ApiSurfaceEntry> = [];
  const removed: Array<ApiSurfaceEntry> = [];
  const changed: Array<ApiSurfaceEntry> = [];

  for (const [name, entry] of current.entries()) {
    const before = baseline.get(name);
    if (!before) {
      added.push(entry);
      continue;
    }
    if (before.signature !== entry.signature) {
      changed.push(entry);
    }
  }
  for (const [name, entry] of baseline.entries()) {
    if (!current.has(name)) {
      removed.push(entry);
    }
  }

  added.sort((a, b) => a.name.localeCompare(b.name));
  removed.sort((a, b) => a.name.localeCompare(b.name));
  changed.sort((a, b) => a.name.localeCompare(b.name));

  return { added, removed, changed };
}

/**
 * Build an {@link AuditCheckRunner} that emits API-diff findings by
 * comparing a baseline rollup to a current rollup. Severity mapping
 * (per the parent epic's drift-handling matrix):
 *
 * - **added** — `mechanical`. A new public export is purely
 *   additive; Phase 2 can stub TSDoc and add reference-doc rows
 *   without human input.
 * - **removed** — `advisory`. A removed public export likely needs
 *   a deprecation note in prose; Phase 2 cannot mechanically rewrite
 *   conceptual prose.
 * - **changed** — `advisory`. A signature change may invalidate
 *   inline-code references in docs and warrants human review of any
 *   surrounding prose.
 */
export function createApiDiffCheck(
  options: ApiDiffCheckOptions,
): AuditCheckRunner {
  const baseline = options.baselineRollup;
  const current = options.currentRollup;
  const rollupPath = options.rollupPath ?? "";
  return {
    name: options.name ?? "apiDiff",
    run(_context: AuditCheckRunnerContext): ReadonlyArray<AuditFinding> {
      const diff = diffApiRollups(baseline, current);
      const findings: Array<ApiDiffFinding> = [];

      for (const entry of diff.added) {
        findings.push({
          category: AuditCategory.ApiDiff,
          severity: AuditSeverity.Mechanical,
          location: { file: rollupPath, line: 0 },
          subject: entry.name,
          details: `New public export \`${entry.name}\` not present in baseline rollup.`,
          change: "added",
          symbol: entry.name,
          fixHint: "stub-tsdoc",
        });
      }
      for (const entry of diff.removed) {
        findings.push({
          category: AuditCategory.ApiDiff,
          severity: AuditSeverity.Advisory,
          location: { file: rollupPath, line: 0 },
          subject: entry.name,
          details: `Public export \`${entry.name}\` was present in baseline rollup but is gone in the current rollup.`,
          change: "removed",
          symbol: entry.name,
        });
      }
      for (const entry of diff.changed) {
        findings.push({
          category: AuditCategory.ApiDiff,
          severity: AuditSeverity.Advisory,
          location: { file: rollupPath, line: 0 },
          subject: entry.name,
          details: `Public export \`${entry.name}\` signature changed since baseline rollup.`,
          change: "changed",
          symbol: entry.name,
        });
      }

      return findings;
    },
  };
}

function extractFirstTsFence(rollup: string): string | undefined {
  // Match the first ```ts ... ``` block. api-extractor always uses
  // ```ts (not ```typescript) per its emitted format.
  const lines = rollup.split("\n");
  let start = -1;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line === "```ts" || line === "```typescript") {
      start = i + 1;
      break;
    }
  }
  if (start < 0) {
    return undefined;
  }
  for (let j = start; j < lines.length; j++) {
    if (lines[j].trim() === "```") {
      return lines.slice(start, j).join("\n");
    }
  }
  // Unterminated fence — return everything after the opener.
  return lines.slice(start).join("\n");
}

function countBraceDelta(line: string): number {
  let delta = 0;
  for (const ch of line) {
    if (ch === "{") {
      delta += 1;
    } else if (ch === "}") {
      delta -= 1;
    }
  }
  return delta;
}

function isComplete(
  buffer: string,
  braceDepth: number,
  endsWithSemicolon: boolean,
): boolean {
  // A declaration is complete when brace depth has returned to zero
  // and we ended on a semicolon (function / variable signatures) or
  // on a closing brace (interface / class / type bodies).
  if (braceDepth > 0) {
    return false;
  }
  if (endsWithSemicolon) {
    return true;
  }
  return buffer.trimEnd().endsWith("}");
}

function normalizeWhitespace(s: string): string {
  // Collapse runs of whitespace to a single space, then strip
  // spaces immediately inside parentheses, brackets, and braces
  // and around commas / semicolons / colons. This is what makes
  // the tight `(input: string)` and the loose `( input: string )`
  // compare equal — the cosmetic re-flow produced by api-extractor
  // when a signature wraps to multiple lines should not surface as
  // a `changed` finding.
  return s
    .replace(/\s+/g, " ")
    .replace(/\(\s+/g, "(")
    .replace(/\s+\)/g, ")")
    .replace(/\[\s+/g, "[")
    .replace(/\s+\]/g, "]")
    .replace(/\{\s+/g, "{")
    .replace(/\s+\}/g, "}")
    .replace(/\s+,/g, ",")
    .replace(/\s+;/g, ";")
    .replace(/\s+:/g, ":")
    .trim();
}

const DECLARATION_KEYWORDS: ReadonlyArray<string> = [
  "function",
  "class",
  "interface",
  "type",
  "enum",
  "const",
  "let",
  "var",
  "namespace",
  "abstract",
  "default",
  "async",
];

function extractDeclarationName(declaration: string): string | undefined {
  const tokens = declaration.split(/\s+/);
  if (tokens.length === 0 || tokens[0] !== "export") {
    return undefined;
  }
  let cursor = 1;
  while (
    cursor < tokens.length &&
    DECLARATION_KEYWORDS.includes(tokens[cursor])
  ) {
    cursor += 1;
  }
  if (cursor >= tokens.length) {
    return undefined;
  }
  const candidate = tokens[cursor];
  if (candidate.startsWith("{")) {
    // `export { Foo, Bar }`. Return the first name; the caller may
    // expand the list separately when more than one is present.
    const stripped = candidate.replace(/^\{/, "").replace(/[},].*$/, "");
    return stripped || undefined;
  }
  // Strip trailing punctuation that may attach to the name in
  // function declarations (`fooApi(...): void;`).
  const cleaned = candidate.replace(/[<(:;].*$/, "");
  return cleaned || undefined;
}

function extractExportListNames(declaration: string): Array<string> {
  // Captures the contents between `{` and the first `}` in an
  // `export { A, B as C }` clause. Re-exports with `as` aliasing
  // surface the alias as the public name.
  const open = declaration.indexOf("{");
  const close = declaration.indexOf("}", open);
  if (open < 0 || close < 0) {
    return [];
  }
  const inside = declaration.slice(open + 1, close);
  const names: Array<string> = [];
  for (const part of inside.split(",")) {
    const token = part.trim();
    if (!token) {
      continue;
    }
    const asMatch = token.match(/\sas\s+(\S+)$/);
    if (asMatch) {
      names.push(asMatch[1]);
    } else {
      names.push(token);
    }
  }
  return names;
}
